import { CREATE_SHIELD_TWO_MUTATION } from "@/graphql/mutations/forgeMutations";
import { useAuth } from "@/hooks/auth/useAuth";
import { useMutation } from "@apollo/client";
import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { SketchPicker } from "react-color";
import { useNavigate } from "react-router-dom";

interface SVGEditorShieldTwoProps {
    svgContent: string;
}

const MissingPaintModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
}> = ({ isOpen, onClose }) => {
    return (
        <Transition show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="bg-brand_charcoal/80 fixed inset-0 transition-opacity" />
                </Transition.Child>
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="bg-brand_charcoal border-brand relative z-50 transform overflow-hidden rounded-lg border-4 p-6 text-left shadow-xl transition-all sm:max-w-sm">
                                <Dialog.Title className="text-brand mb-4 text-center text-2xl font-bold">
                                    Missing Paint!
                                </Dialog.Title>
                                <div className="flex flex-col items-center">
                                    <p className="text-brand text-center text-lg">
                                        Sorry, your shield seems to be missing
                                        some paint! Please try again and
                                        re-submit. Skál!
                                    </p>
                                </div>
                                <div className="mt-6 flex justify-end">
                                    <button
                                        onClick={onClose}
                                        className="rounded bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
                                    >
                                        OK
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

const SubmitShieldConfirmationModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
}> = ({ isOpen, onClose, onConfirm }) => {
    return (
        <Transition show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="bg-brand_charcoal/80 fixed inset-0 transition-opacity" />
                </Transition.Child>
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="bg-brand_charcoal border-brand relative z-50 transform overflow-hidden rounded-lg border-4 p-6 text-left shadow-xl transition-all sm:max-w-sm">
                                <Dialog.Title className="text-brand mb-4 text-center text-2xl font-bold">
                                    Forge Your Second Shield!
                                </Dialog.Title>
                                <div className="flex flex-col items-center">
                                    <p className="text-brand text-center text-lg">
                                        You are about to submit your second
                                        shield to WALLHALLA! This commemorates
                                        your 12 months in the GILDI of the
                                        ÁRMAÐR. Please make sure you double
                                        check your paint work before submission
                                        as this process is final! Skál!
                                    </p>
                                </div>
                                <div className="mt-6 flex justify-end space-x-2">
                                    <button
                                        onClick={onClose}
                                        className="rounded bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={onConfirm}
                                        className="rounded bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
                                    >
                                        Confirm
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

const SVGEditorShieldTwo: React.FC<SVGEditorShieldTwoProps> = ({
    svgContent,
}) => {
    const [editableSvg, setEditableSvg] = useState(svgContent);
    const [selectedColor, setSelectedColor] = useState("#ff0000");
    const [history, setHistory] = useState<
        { id: string; previousColor: string }[]
    >([]);
    const [redoHistory, setRedoHistory] = useState<
        { id: string; previousColor: string }[]
    >([]);
    const [recentColors, setRecentColors] = useState<string[]>([
        "#ffffff",
        "#000000",
        "#ff0000",
        "#00ff00",
        "#0000ff",
        "#ffff00",
    ]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isMissingPaintModalOpen, setIsMissingPaintModalOpen] =
        useState(false);
    const svgContainerRef = useRef<HTMLDivElement>(null);
    const selectedColorRef = useRef(selectedColor);
    const navigate = useNavigate();
    const { fetchUserData } = useAuth();

    const [createShieldTwo, { loading }] = useMutation(
        CREATE_SHIELD_TWO_MUTATION,
        {
            onCompleted: async (data) => {
                if (data.createShieldTwo.success) {
                    try {
                        await fetchUserData();
                        navigate("/dashboard");
                    } catch (error) {
                        console.error("Failed to fetch user data:", error);
                        setErrorMessage(
                            "An error occurred while fetching user data.",
                        );
                    }
                } else {
                    setErrorMessage(
                        data.createShieldTwo.message || "An error occurred.",
                    );
                }
            },
            onError: (error) => {
                setErrorMessage(
                    error.message || "An unexpected error occurred.",
                );
            },
        },
    );

    useEffect(() => {
        selectedColorRef.current = selectedColor;
    }, [selectedColor]);

    useEffect(() => {
        if (!svgContainerRef.current || !editableSvg) return;
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(editableSvg, "image/svg+xml");

        const paths = svgDoc.querySelectorAll(".cls-2");
        paths.forEach((path, index) => {
            const id = `path-${index}`;
            path.setAttribute("id", id);

            if (
                !path.hasAttribute("fill") ||
                path.getAttribute("fill") === "none"
            ) {
                path.setAttribute("fill", "#ffffff");
            }

            path.setAttribute(
                "style",
                "cursor: pointer; transition: fill 0.2s ease;",
            );
            path.addEventListener("click", () => handlePathClick(id));
        });

        svgContainerRef.current.innerHTML = "";
        svgContainerRef.current.appendChild(svgDoc.documentElement);
    }, [editableSvg]);

    const handlePathClick = (id: string) => {
        if (!svgContainerRef.current) return;
        const path = svgContainerRef.current.querySelector(`#${id}`);
        if (path) {
            const previousColor = path.getAttribute("fill") || "#ffffff";

            setHistory((prev) => [...prev, { id, previousColor }]);
            setRedoHistory([]);

            path.setAttribute("fill", selectedColorRef.current);

            const svgElement = svgContainerRef.current.querySelector("svg");
            if (svgElement) {
                const serializer = new XMLSerializer();
                setEditableSvg(serializer.serializeToString(svgElement));
            }
        }
    };

    const handleColorChangeComplete = (color: { hex: string }) => {
        const newColor = color.hex;
        setSelectedColor(newColor);

        setRecentColors((prevColors) => {
            const updatedColors = [
                newColor,
                ...prevColors.filter((c) => c !== newColor),
            ].slice(0, 6);
            return updatedColors;
        });
    };

    const handleUndo = () => {
        if (history.length === 0) return;

        const lastAction = history[history.length - 1];
        const path = svgContainerRef.current?.querySelector(
            `#${lastAction.id}`,
        );
        if (path) {
            const currentColor = path.getAttribute("fill") || "#ffffff";
            setRedoHistory((prev) => [
                ...prev,
                { id: lastAction.id, previousColor: currentColor },
            ]);
            path.setAttribute("fill", lastAction.previousColor);

            const svgElement = svgContainerRef.current?.querySelector("svg");
            if (svgElement) {
                const serializer = new XMLSerializer();
                setEditableSvg(serializer.serializeToString(svgElement));
            }
        }
        setHistory((prev) => prev.slice(0, -1));
    };

    const handleRedo = () => {
        if (redoHistory.length === 0) return;

        const lastRedo = redoHistory[redoHistory.length - 1];
        const path = svgContainerRef.current?.querySelector(`#${lastRedo.id}`);
        if (path) {
            const currentColor = path.getAttribute("fill") || "#ffffff";
            setHistory((prev) => [
                ...prev,
                { id: lastRedo.id, previousColor: currentColor },
            ]);
            path.setAttribute("fill", lastRedo.previousColor);

            const svgElement = svgContainerRef.current?.querySelector("svg");
            if (svgElement) {
                const serializer = new XMLSerializer();
                setEditableSvg(serializer.serializeToString(svgElement));
            }
        }
        setRedoHistory((prev) => prev.slice(0, -1));
    };

    const handleClear = () => {
        if (!svgContainerRef.current) return;

        const paths = svgContainerRef.current.querySelectorAll(".cls-2");
        paths.forEach((path) => {
            path.setAttribute("fill", "#ffffff");
        });

        const svgElement = svgContainerRef.current.querySelector("svg");
        if (svgElement) {
            const serializer = new XMLSerializer();
            setEditableSvg(serializer.serializeToString(svgElement));
        }

        setHistory([]);
        setRedoHistory([]);
    };

    const handleSubmit = () => {
        if (!editableSvg) {
            setErrorMessage("SVG content is missing.");
            return;
        }

        try {
            const base64Svg = `data:image/svg+xml;base64,${btoa(editableSvg)}`;
            createShieldTwo({
                variables: {
                    imageData: base64Svg,
                },
            });
        } catch (error) {
            setErrorMessage("Failed to encode the SVG. Please try again.");
        }
    };

    return (
        <div className="mt-20 grid grid-cols-5">
            <div className="border-brand bg-brand_charcoal bg-opacity-70 col-span-3 col-start-2 grid grid-cols-2 gap-4 rounded-lg border-4 p-10">
                {/* SVG Container */}
                <div ref={svgContainerRef} className=""></div>

                {/* Color Picker */}
                <div className="flex flex-col items-center justify-center space-y-4">
                    <SketchPicker
                        color={selectedColor}
                        onChangeComplete={handleColorChangeComplete}
                        presetColors={recentColors}
                        width="65%"
                    />

                    {/* Color Pill */}
                    <div
                        className="h-10 w-10 rounded-full border border-gray-300"
                        style={{ backgroundColor: selectedColor }}
                        title={`Selected Color: ${selectedColor}`}
                    ></div>
                </div>

                {/* Error Alert */}
                {errorMessage && (
                    <div className="col-span-2 mb-4 rounded-sm bg-red-500 p-4 text-white">
                        <p>{errorMessage}</p>
                    </div>
                )}

                {/* Buttons */}
                <div className="col-span-2 flex items-center justify-center space-x-4">
                    {/* Back Button */}
                    <button
                        onClick={() => navigate("/forge-shield-two")}
                        className="rounded-sm bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
                    >
                        Back
                    </button>
                    <button
                        onClick={handleUndo}
                        className="rounded-sm bg-yellow-500 px-4 py-2 text-white hover:bg-yellow-600"
                    >
                        Undo
                    </button>
                    <button
                        onClick={handleRedo}
                        className="rounded-sm bg-green-500 px-4 py-2 text-white hover:bg-green-600"
                    >
                        Redo
                    </button>
                    <button
                        onClick={handleClear}
                        className="rounded-sm bg-red-500 px-4 py-2 text-white hover:bg-red-600"
                    >
                        Clear
                    </button>
                    <button
                        onClick={() => {
                            const paths =
                                svgContainerRef.current?.querySelectorAll(
                                    ".cls-2",
                                );
                            const allWhite =
                                paths &&
                                paths.length > 0 &&
                                Array.from(paths).every(
                                    (p) =>
                                        (
                                            p.getAttribute("fill") || "#ffffff"
                                        ).toLowerCase() === "#ffffff",
                                );
                            if (allWhite) {
                                setIsMissingPaintModalOpen(true);
                                return;
                            }
                            setIsModalOpen(true);
                        }}
                        disabled={loading}
                        className={`rounded px-4 py-2 text-white ${
                            loading
                                ? "cursor-not-allowed bg-gray-400"
                                : "bg-purple-600 hover:bg-purple-700"
                        }`}
                    >
                        {loading ? "Submitting..." : "Submit"}
                    </button>
                </div>
            </div>
            {/* Missing Paint Modal */}
            <MissingPaintModal
                isOpen={isMissingPaintModalOpen}
                onClose={() => setIsMissingPaintModalOpen(false)}
            />
            {/* Confirmation Modal */}
            <SubmitShieldConfirmationModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={() => {
                    handleSubmit();
                    setIsModalOpen(false);
                }}
            />
        </div>
    );
};

export default SVGEditorShieldTwo;
